// nativePush.ts — Natiivipush (7.8.2026)
// Capacitor-natiivisovelluksen (iOS/Android) push-rekisteröinti. Web-push
// (pushClient.ts) ei toimi natiivikuoren WKWebView'ssä — natiivialustalla
// rekisteröidään APNs/FCM-laitetoken backendiin, ja backend lähettää
// notifikaatiot APNs:n kautta web-pushin rinnalla.
//
// HUOM: push-entitlement vaatii maksullisen Apple Developer -tilin.
// Ilmaisella kehitysprofiililla rekisteröinti epäonnistuu — kaikki virheet
// vaimennetaan, joten appi toimii normaalisti kunnes entitlement on olemassa.
import { Capacitor } from '@capacitor/core';
import { authFetch } from '../api/client';

/** Onko käynnissä Capacitor-natiivisovelluksessa (ei selain/PWA). */
export function isNativeApp(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Rekisteröi natiivipush kirjautuneelle käyttäjälle. Kutsutaan shellin
 * mountissa — no-op selaimessa. Ei koskaan heitä.
 */
export async function registerNativePush(): Promise<void> {
  if (!isNativeApp()) return;
  try {
    // Dynaaminen import — plugin ladataan vain natiivialustalla
    const { PushNotifications } = await import('@capacitor/push-notifications');

    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt') {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== 'granted') return;

    await PushNotifications.removeAllListeners();

    await PushNotifications.addListener('registration', async token => {
      try {
        const platform = Capacitor.getPlatform() === 'ios' ? 'APNS' : 'FCM';
        await authFetch('/push-notifications/native-token', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ platform, token: token.value, deviceInfo: navigator.userAgent }),
        });
      } catch { /* hiljainen fail — yritetään seuraavassa mountissa */ }
    });

    await PushNotifications.addListener('registrationError', () => {
      // Odotettu ilman push-entitlementia (ilmainen kehitysprofiili) — hiljaa.
    });

    // Notifikaation avaus → navigoi payloadin url:iin (APNs custom key)
    await PushNotifications.addListener('pushNotificationActionPerformed', action => {
      const url = (action.notification.data as { url?: string } | undefined)?.url;
      if (url && url.startsWith('/')) window.location.assign(url);
    });

    await PushNotifications.register();
  } catch { /* plugin puuttuu tai alusta ei tue — hiljainen fail */ }
}
